import { alpha, Box, Tab, Tabs } from "@mui/material";
import type { FC, ReactElement } from "react";
import { Link, Route, Routes, useLocation } from "react-router";
import { PageContent, type PageContentProps } from "../../layout/components/page-content";

type RouteTab =
  | {
      type: "inferred";
      component: React.JSX.Element;
      data: {
        name: string;
        icon?: ReactElement;
        fullBleed?: boolean;
        queryParamsToRemove?: string[];
      };
    }
  | {
      type: "explicit";
      component: React.JSX.Element;
      data: {
        name: string | React.JSX.Element;
        icon?: ReactElement;
        fullBleed?: boolean;
        route: string;
        queryParamsToRemove?: string[];
      };
    };

interface RouteTabsProps extends PageContentProps {
  base: string;
  defaultPage: React.JSX.Element;
  tabs: RouteTab[];
  centered?: boolean;
  preserveQuery?: boolean;
  appearance?: "default" | "toolbar";
  showTabs?: boolean;
  renderFirstTabAtBase?: boolean;
}
export const RouteTabs: FC<RouteTabsProps> = ({
  defaultPage,
  tabs,
  base,
  centered,
  preserveQuery,
  appearance = "default",
  showTabs = true,
  renderFirstTabAtBase = false,
  ...pageContentProps
}) => {
  const location = useLocation();
  const relativePath = getRelativePath(location.pathname, base);
  const selectedTab =
    tabs.find((tab) => {
      const route = getTabRoute(tab).toLowerCase();
      const path = relativePath.toLowerCase();
      return path === route || path.startsWith(`${route}/`);
    }) ?? (renderFirstTabAtBase && relativePath === "" ? tabs[0] : undefined);

  return (
    <PageContent
      {...pageContentProps}
      fullWidth={appearance === "toolbar" || pageContentProps.fullWidth}
      maxWidth={appearance === "toolbar" ? undefined : pageContentProps.maxWidth}
      sx={appearance === "toolbar" ? { m: 0, width: "100%" } : pageContentProps.sx}
    >
      <Tabs
        indicatorColor="primary"
        scrollButtons={appearance === "toolbar" ? false : "auto"}
        textColor="primary"
        value={selectedTab ? getTabRoute(selectedTab).toUpperCase() : false}
        variant={centered ? "standard" : "scrollable"}
        centered={centered}
        sx={
          appearance === "toolbar"
            ? {
                display: showTabs ? undefined : "none",
                height: 56,
                minHeight: 56,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                "& .MuiTabs-indicator": { height: 2 },
              }
            : { display: showTabs ? undefined : "none", mt: 3 }
        }
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.type === "inferred" ? tab.data.name : tab.data.route}
            style={{ pointerEvents: "auto" }}
            label={tab.data.name}
            icon={tab.data.icon}
            iconPosition="start"
            value={getTabRoute(tab).toUpperCase()}
            sx={
              appearance === "toolbar"
                ? {
                    height: 56,
                    minHeight: 56,
                    minWidth: 0,
                    px: 2,
                    py: 1,
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    lineHeight: 1.57,
                    textTransform: "none",
                    "&.Mui-selected": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                    },
                    "& .MuiTab-iconWrapper": { mr: 0.5, mb: "0 !important", fontSize: 16 },
                  }
                : { ml: 2 }
            }
            component={Link}
            to={`${base}${(tab.type === "inferred"
              ? normalizeName(tab.data.name)
              : tab.data.route
            ).toLowerCase()}${getPreservedSearch(
              location.search,
              preserveQuery,
              tab.data.queryParamsToRemove,
            )}`}
          />
        ))}
      </Tabs>
      <Box
        sx={
          appearance === "toolbar"
            ? selectedTab?.data.fullBleed
              ? { width: "100%" }
              : {
                  width: "calc(100% - var(--page-px) - var(--page-px))",
                  maxWidth: 1440,
                  mx: "auto",
                  px: 2,
                  pt: showTabs ? 4 : 0,
                }
            : { mt: 4 }
        }
      >
        {appearance === "toolbar" ? (
          relativePath === "" && !renderFirstTabAtBase ? (
            defaultPage
          ) : (
            selectedTab?.component
          )
        ) : (
          <Routes>
            <Route path="/" element={defaultPage} />
            {tabs.map((tab) => (
              <Route
                key={tab.type === "inferred" ? tab.data.name : tab.data.route}
                path={`${getTabRoute(tab).toLowerCase()}/*`}
                element={tab.component}
              />
            ))}
          </Routes>
        )}
      </Box>
    </PageContent>
  );
};

function getTabRoute(tab: RouteTab): string {
  return tab.type === "inferred" ? normalizeName(tab.data.name) : tab.data.route;
}

function getRelativePath(pathname: string, base: string): string {
  const baseWithoutTrailingSlash = base.replace(/\/$/, "");
  if (pathname === baseWithoutTrailingSlash || pathname === base) return "";
  if (!pathname.startsWith(base)) return pathname;
  return pathname.slice(base.length).replace(/^\//, "").replace(/\/$/, "");
}

function normalizeName(name: string): string {
  return encodeURIComponent(name.split(" ").join("_"));
}

function getPreservedSearch(
  search: string,
  preserveQuery?: boolean,
  queryParamsToRemove: string[] = [],
): string {
  if (!preserveQuery) return "";

  const searchParams = new URLSearchParams(search);
  queryParamsToRemove.forEach((queryParam) => searchParams.delete(queryParam));
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
}
